import { buildAllocation, type Holding, type ValuedHolding } from '@porttang/core';
import { useMemo, useState } from 'react';
import { formatSharePercent, formatTHB } from '../../shared/format';
import { Card, SectionLabel, tintFor } from '../../shared/pixel';

/**
 * กราฟวงกลมวาดด้วย SVG ล้วน ไม่ใช้ chart library
 *
 * Recharts เพิ่มขนาด bundle ราว 350 KB บน bundle ที่ใหญ่อยู่แล้ว เพื่อวาดวงแหวนวงเดียว
 * โดนัทคือวงกลมที่ใส่ `stroke-dasharray` ให้ยาวตามสัดส่วน แล้วเลื่อนด้วย `stroke-dashoffset`
 * ซึ่งเบราว์เซอร์วาดให้คมทุกขนาดโดยไม่ต้องคำนวณ path เอง
 *
 * วงแหวนบอกภาพรวมได้เร็ว แต่กะขนาดมุมให้แม่นไม่ได้ — ตัวเลขจริงจึงต้องอยู่ครบทุกบรรทัด
 * ในการ์ดรายละเอียดข้าง ๆ ไม่ใช่ให้อ่านจากรูปทรงอย่างเดียว
 */

const RADIUS = 60;
const STROKE = 24;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type GroupBy = 'symbol' | 'assetType' | 'platform';

const ASSET_TYPE_LABELS: Record<string, string> = {
  stock: 'หุ้น',
  etf: 'ETF',
  crypto: 'คริปโต',
  cash: 'เงินสด',
};

const GROUPINGS: { value: GroupBy; label: string; keyOf: (holding: Holding) => string }[] = [
  { value: 'symbol', label: 'รายตัว', keyOf: (h) => h.symbol },
  {
    value: 'assetType',
    label: 'ประเภท',
    keyOf: (h) => ASSET_TYPE_LABELS[h.assetType] ?? h.assetType,
  },
  { value: 'platform', label: 'แพลตฟอร์ม', keyOf: (h) => h.platform },
];

/** `hidePlatform` — ใช้บนแท็บพอร์ตแยก ซึ่งแบ่งตามแพลตฟอร์มแล้วได้วงเต็มวงเดียวไม่มีความหมาย */
export function AllocationChart({
  items,
  hidePlatform = false,
}: {
  items: readonly ValuedHolding[];
  hidePlatform?: boolean;
}) {
  const [groupBy, setGroupBy] = useState<GroupBy>('symbol');

  const options = hidePlatform ? GROUPINGS.filter((g) => g.value !== 'platform') : GROUPINGS;
  // เลือกแพลตฟอร์มค้างไว้แล้วสลับเข้าแท็บพอร์ตแยก — ถอยกลับไปรายตัวแทนการแสดงวงที่ซ่อนปุ่มไปแล้ว
  const grouping = options.find((g) => g.value === groupBy) ?? options[0]!;
  const allocation = useMemo(() => buildAllocation(items, grouping.keyOf), [items, grouping]);

  // สะสมความยาวไปเรื่อย ๆ เพื่อวางจุดเริ่มของแต่ละส่วนต่อจากส่วนก่อนหน้า
  let consumed = 0;
  const arcs = allocation.slices.map((slice) => {
    const share = Number(slice.sharePct ?? 0) / 100;
    const length = CIRCUMFERENCE * share;
    const arc = {
      key: slice.key,
      // สีผูกกับชื่อ ไม่ใช่ลำดับ — ตรงกับตราสินทรัพย์ในตาราง และไม่สลับกันเวลาเรียงใหม่
      color: tintFor(slice.key),
      dashArray: `${length} ${CIRCUMFERENCE - length}`,
      dashOffset: -consumed,
    };
    consumed += length;
    return arc;
  });

  /*
    ยังไม่มีรายการไหนตีมูลค่าได้เลย — เดิมคืน null ทำให้คอลัมน์ขวาของแดชบอร์ดว่างเป็นรู
    และการ์ดยอดรวมก็ไม่ขยายมาแทน บอกไปเลยว่าทำไมยังว่างและต้องทำอะไรต่อ ดีกว่าปล่อยหาย
  */
  if (allocation.slices.length === 0) {
    return (
      <Card className="p-5">
        <SectionLabel>ALLOCATION</SectionLabel>
        <p className="mt-3 text-sm text-ink-300">ยังคำนวณสัดส่วนไม่ได้</p>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-500">
          ต้องมีราคาของสินทรัพย์อย่างน้อยหนึ่งรายการก่อน กดรีเฟรชราคาแล้วสัดส่วนจะขึ้นตรงนี้
        </p>
      </Card>
    );
  }

  return (
    /*
      แยกเป็นสองการ์ด: วงแหวนอยู่ซ้าย รายละเอียดรายบรรทัดอยู่ขวา

      เดิมทั้งสองอย่างเบียดกันอยู่ในการ์ดเดียวของคอลัมน์แคบ วงแหวนจึงโตได้แค่ 160px
      พอแยกการ์ดแล้วแต่ละฝั่งได้ความกว้างเต็มที่ วงแหวนโตขึ้นได้โดยไม่ไปบีบรายชื่อให้ตกบรรทัด
      จอแคบกว่า ~680px จะพับลงมาเรียงบนล่างเองด้วย flex-wrap
    */
    <div className="flex flex-wrap gap-5">
      <Card className="flex-[1_1_340px] p-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <SectionLabel>ALLOCATION</SectionLabel>

          <div className="flex gap-1 rounded-lg border border-line-strong bg-sunken p-1">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setGroupBy(option.value)}
                className={[
                  'rounded-[5px] px-2.5 py-1 text-xs transition',
                  grouping.value === option.value
                    ? 'bg-brand-sel text-brand-text'
                    : 'text-ink-400 hover:text-ink-100',
                ].join(' ')}
              >
                {option.label}
              </button>
            ))}
          </div>
        </header>

        <div className="mt-6 flex justify-center">
          <div className="relative">
            <svg
              viewBox="0 0 160 160"
              className="size-56 sm:size-72"
              role="img"
              aria-label={`สัดส่วนพอร์ตแบ่งตาม${grouping.label}`}
            >
              {/* หมุน -90 องศาเพื่อให้ส่วนแรกเริ่มที่ตำแหน่ง 12 นาฬิกา ไม่ใช่ 3 นาฬิกา */}
              <g transform="rotate(-90 80 80)">
                {arcs.map((arc) => (
                  <circle
                    key={arc.key}
                    cx="80"
                    cy="80"
                    r={RADIUS}
                    fill="none"
                    strokeWidth={STROKE}
                    // สีมาจาก CSS variable — ใส่ผ่าน style ไม่ใช่ attribute `stroke`
                    // เพราะ var() ใน presentation attribute ไม่ได้รองรับทุกเบราว์เซอร์
                    style={{ stroke: arc.color }}
                    strokeDasharray={arc.dashArray}
                    strokeDashoffset={arc.dashOffset}
                  />
                ))}
              </g>
            </svg>

            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="pixel-label">TOTAL</span>
              <span className="mt-1.5 font-mono text-[17px] text-ink-100">
                {formatTHB(allocation.totalTHB)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="flex-[1_1_340px] p-5">
        <SectionLabel>BREAKDOWN · {allocation.slices.length} GROUPS</SectionLabel>

        <ul className="mt-4 flex flex-col gap-2.5 border-t border-line pt-4">
          {allocation.slices.map((slice) => (
            <li key={slice.key} className="flex items-center gap-3 text-[13px]">
              <span
                aria-hidden
                className="size-2.5 shrink-0"
                style={{ backgroundColor: tintFor(slice.key) }}
              />
              <span className="min-w-0 flex-1 truncate text-ink-200">{slice.key}</span>
              <span className="shrink-0 font-mono text-[11px] text-ink-500">
                {formatTHB(slice.valueTHB)}
              </span>
              <span className="w-14 shrink-0 text-right font-mono text-ink-300">
                {formatSharePercent(slice.sharePct)}
              </span>
            </li>
          ))}
        </ul>

        {allocation.excludedCount > 0 && (
          <p className="mt-4 flex items-start gap-2.5 rounded-lg border border-dashed border-line-dash px-3.5 py-2.5 text-xs leading-relaxed text-ink-300">
            <span aria-hidden className="mt-1 size-2.5 shrink-0 animate-blink bg-warn" />
            <span>
              ไม่ได้นับ {allocation.excludedCount} รายการที่ยังตีมูลค่าไม่ได้ —
              สัดส่วนข้างบนคิดจากเฉพาะรายการที่มีราคาแล้วเท่านั้น
            </span>
          </p>
        )}
      </Card>
    </div>
  );
}
